import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../../redux/errors.slice"
import type { RootState } from "../../../../store.ts"
import { unknownError } from "../../../../utils/handlers/handle-error"
import i18n from "../../../../i18n.ts"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import * as contractServices from "../../../Contracts/api/services"
import * as services from "../../api/services"
import type {
  Document,
  DocumentRequestData
} from "../../types/document.types.ts"
import { documentTemplateSliceName } from "../document-templates.constants.ts"

export const fetchContractDocumentById = createAsyncThunk<Document, string>(
  `${documentTemplateSliceName}/fetchContractDocumentById`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getContractDocumentById(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data!
  }
)

export const createContractDocument = createAsyncThunk<
  Document,
  DocumentRequestData
>(
  `${documentTemplateSliceName}/createContractDocument`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createContractDocument(data)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Document created successfully.")

    return response!.data!.details.data
  }
)

export const updateContractDocument = createAsyncThunk<
  Document,
  { id: string; data: Partial<Document> }
>(
  `${documentTemplateSliceName}/updateContractDocument`,
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    const response = await services.updateContractDocument(id, data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Document updated successfully.")

    return response!.data!.details.data
  }
)

export const saveContractDocument = createAsyncThunk<void, string>(
  `${documentTemplateSliceName}/saveContractDocument`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.saveContractDocument(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Document saved successfully.")
  }
)

export const sendContractDocumentToApproval = createAsyncThunk<
  void,
  string,
  { state: RootState }
>(
  `${documentTemplateSliceName}/sendContractDocumentToApproval`,
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const saveRes = await services.saveContractDocument(id)
      if (!saveRes.ok) {
        dispatch(errorSave(saveRes.data?.details))
        return rejectWithValue(null)
      }

      const attachmentId = (
        saveRes.data as unknown as { details?: { data?: { id?: string } } }
      )?.details?.data?.id
      if (!attachmentId) throw new Error()

      const submitRes = await contractServices.submitForReview({
        contractId: id,
        attachmentId
      })
      if (!submitRes.ok) {
        dispatch(errorSave((submitRes.data as { details?: unknown })?.details))
        return rejectWithValue(null)
      }

      showResponseMessage({}, i18n.t("documentApproval.documentSent"))
    } catch (e: unknown) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }
  }
)
